import React from "react";
import { ArrowRight, RotateCcw, LayoutGrid, Skull, DoorOpen, CheckCircle2, ShieldCheck } from "lucide-react";
import { fileNo, verdictLabel } from "../data/sporeGames";
import { sporeBtn } from "./SporeShell";

const VerdictChip = ({ entry }) =>
  entry.infected ? (
    <span className="border border-red-500/60 text-red-300 px-3 py-1.5 flex items-center gap-2"><Skull className="h-3.5 w-3.5" /> EST. SURVIVAL {entry.survival}</span>
  ) : (
    <span className="border border-emerald-500/60 text-emerald-300 px-3 py-1.5 flex items-center gap-2"><ShieldCheck className="h-3.5 w-3.5" /> NO SPORE ACTIVITY</span>
  );

export const RevealScreen = ({ doctor, entry, seconds, best, revealedCount, total, onNext, onReplay, onRoster }) => (
  <section
    data-testid="reveal-screen"
    data-verdict={entry.infected ? "infected" : "clear"}
    className={`rounded-sm border bg-[#121824] overflow-hidden spore-fade-up ${entry.infected ? "border-red-500/60 spore-alarm" : "border-emerald-500/50 shadow-[0_0_40px_rgba(16,185,129,0.18)]"}`}
  >
    <div className={`${entry.infected ? "bg-[#FF2E63]" : "bg-emerald-400"} text-black font-tac text-xs tracking-[0.3em] px-5 py-2 flex justify-between`}>
      <span>FILE UNSEALED // VERDICT</span>
      <span data-testid="reveal-progress">{revealedCount} / {total} REVEALED</span>
    </div>
    <div className="p-6 sm:p-10 space-y-8">
      <div>
        <p className="font-tac text-xs tracking-[0.3em] text-cyan-300/80">{fileNo(entry)} // {entry.category.toUpperCase()}</p>
        <h1 data-testid="revealed-verdict-line" className="font-display uppercase font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-3">
          <span data-testid="revealed-symptom" className="text-slate-50 spore-reveal inline-block">{entry.symptom}</span>
          <span className="text-slate-500"> = </span>
          <span
            data-testid="revealed-verdict"
            data-verdict={entry.infected ? "infected" : "clear"}
            className={`spore-stamp-in ${entry.infected ? "text-[#FF2E63] spore-glow-red" : "text-emerald-400 [text-shadow:0_0_28px_rgba(16,185,129,0.45)]"}`}
          >
            {verdictLabel(entry)}{entry.infected ? "!!" : ""}
          </span>
        </h1>
        <div className="flex flex-wrap gap-3 mt-5 font-tac text-xs">
          <VerdictChip entry={entry} />
          <span data-testid="reveal-time" className="border border-emerald-500/50 text-emerald-300 px-3 py-1.5 flex items-center gap-2">
            <CheckCircle2 className="h-3.5 w-3.5" /> REVEALED IN {seconds.toFixed(1)}s · BEST {best.toFixed(1)}s
          </span>
        </div>
      </div>
      <figure className="flex gap-4 items-start border-l-2 border-slate-700 pl-5">
        <img src={doctor.image} alt={doctor.name} className="w-14 h-14 rounded-sm object-cover object-top grayscale contrast-125 border border-slate-700" />
        <div>
          <blockquote data-testid="reveal-finding" className="text-slate-200 text-base sm:text-lg leading-relaxed">“{entry.finding}”</blockquote>
          <figcaption className="font-tac text-xs text-slate-500 mt-2">— {doctor.name}, leaving for the next quarantine wing</figcaption>
        </div>
      </figure>
      <div className="flex flex-col sm:flex-row gap-3">
        {onNext ? (
          <button type="button" data-testid="reveal-next-btn" onClick={onNext} className={`${sporeBtn.primary} sm:flex-1 py-3`}>
            Next scientist <ArrowRight className="h-4 w-4" />
          </button>
        ) : (
          <p data-testid="all-revealed-msg" className="sm:flex-1 font-tac text-sm text-emerald-300 border border-emerald-500/50 px-4 py-3 text-center">
            ALL {total} FILES UNSEALED. CONTAINMENT REPORT COMPLETE.
          </p>
        )}
        <button type="button" data-testid="reveal-replay-btn" onClick={onReplay} className={`${sporeBtn.ghost} py-3`}>
          <RotateCcw className="h-4 w-4" /> Replay
        </button>
        <button type="button" data-testid="reveal-roster-btn" onClick={onRoster} className={`${sporeBtn.ghost} py-3`}>
          <LayoutGrid className="h-4 w-4" /> Roster
        </button>
      </div>
    </div>
  </section>
);

export const MovedOnScreen = ({ doctor, onRetry, onBack }) => (
  <section data-testid="moved-on-screen" className="rounded-sm border border-slate-700 bg-[#121824] p-6 sm:p-10 space-y-6 spore-fade-up text-center">
    <DoorOpen className="h-12 w-12 text-red-400 mx-auto spore-flicker" />
    <div>
      <p className="font-tac text-xs tracking-[0.3em] text-red-300">TIME EXPIRED</p>
      <h1 className="font-display uppercase font-bold text-3xl sm:text-4xl text-slate-100 mt-2">{doctor.name} has moved on</h1>
      <p className="text-slate-400 mt-3">
        Their case file remains sealed. They'll be back on their next rotation.
      </p>
    </div>
    <div className="flex flex-col sm:flex-row gap-3 justify-center">
      <button type="button" data-testid="moved-on-retry-btn" onClick={onRetry} className={`${sporeBtn.primary} py-3 px-6`}>
        <RotateCcw className="h-4 w-4" /> Try again
      </button>
      <button type="button" data-testid="moved-on-back-btn" onClick={onBack} className={`${sporeBtn.ghost} py-3 px-6`}>
        Back to briefing
      </button>
    </div>
  </section>
);
